/**
 * sRGB ↔ CIE-LAB colour conversion utilities.
 *
 * Pure functions - no DOM, no globals beyond Math.*
 * Reference illuminant: D65. Standard IEC 61966-2-1 sRGB primaries.
 *
 * Swift translatability (A6): all constants are module-level literals;
 * no closures capture mutable state; all functions are free functions.
 */

export type Rgb = [number, number, number]; // each channel 0..255 integer
export type Lab = [number, number, number]; // L in 0..100, a/b approx −128..128

// ─── D65 white point ───────────────────────────────────────────────────────
const WX = 0.95047;
const WY = 1.0;
const WZ = 1.08883;

// ─── f(t) and f⁻¹(t) as per CIE-LAB standard ─────────────────────────────

/** Threshold t₀ = (6/29)³ */
const T0 = (6 / 29) ** 3; // ≈ 0.008856

/** Slope below threshold: 1 / (3 × (6/29)²) */
const SLOPE = 1 / (3 * (6 / 29) ** 2); // ≈ 7.787

function labF(t: number): number {
  return t > T0 ? Math.cbrt(t) : SLOPE * t + 4 / 29;
}

function labFInv(t: number): number {
  return t > 6 / 29 ? t ** 3 : (t - 4 / 29) / SLOPE;
}

// ─── sRGB → linear ────────────────────────────────────────────────────────

function srgbChannelToLinear(c: number): number {
  // c is in [0, 1]
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgbChannel(c: number): number {
  // c is in [0, 1]; clamp to avoid NaN from negative inputs
  const v = Math.max(0, Math.min(1, c));
  return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Convert an 8-bit sRGB triplet to CIE-LAB (D65).
 *
 * @param rgb  [R, G, B] each in 0..255
 * @returns    [L, a, b] where L ∈ 0..100, a/b ∈ approx −128..128
 */
export function srgbToLab(rgb: Rgb): Lab {
  // Step 1 - sRGB 8-bit → linear [0, 1]
  const r = srgbChannelToLinear(rgb[0]! / 255);
  const g = srgbChannelToLinear(rgb[1]! / 255);
  const b = srgbChannelToLinear(rgb[2]! / 255);

  // Step 2 - linear RGB → XYZ (D65 sRGB matrix)
  const x = 0.4124564 * r + 0.3575761 * g + 0.1804375 * b;
  const y = 0.2126729 * r + 0.7151522 * g + 0.0721750 * b;
  const z = 0.0193339 * r + 0.1191920 * g + 0.9503041 * b;

  // Step 3 - XYZ → LAB
  const fx = labF(x / WX);
  const fy = labF(y / WY);
  const fz = labF(z / WZ);

  const L = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const bv = 200 * (fy - fz);

  return [L, a, bv];
}

/**
 * Convert a CIE-LAB triplet (D65) back to an 8-bit sRGB triplet.
 * Output channels are rounded and clamped to 0..255.
 *
 * @param lab  [L, a, b]
 * @returns    [R, G, B] each in 0..255
 */
export function labToSrgb(lab: Lab): Rgb {
  const L = lab[0]!;
  const a = lab[1]!;
  const b = lab[2]!;

  // Step 1 - LAB → XYZ
  const fy = (L + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;

  const x = labFInv(fx) * WX;
  const y = labFInv(fy) * WY;
  const z = labFInv(fz) * WZ;

  // Step 2 - XYZ → linear RGB (D65 sRGB inverse matrix)
  const rLin = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
  const gLin = -0.9692660 * x + 1.8760108 * y + 0.0415560 * z;
  const bLin = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

  // Step 3 - linear → sRGB 8-bit
  const r = Math.round(linearToSrgbChannel(rLin) * 255);
  const g = Math.round(linearToSrgbChannel(gLin) * 255);
  const bOut = Math.round(linearToSrgbChannel(bLin) * 255);

  return [r, g, bOut];
}

/**
 * CIE76 colour difference (simple Euclidean distance in LAB space).
 *
 * Used in Item 21 for RGB-match carry-over: a delta-E ≤ 5.0 means the
 * colours are perceptually close enough to reuse the previous region method.
 *
 * @param a  [L, a, b]
 * @param b  [L, a, b]
 */
export function deltaE(a: Lab, b: Lab): number {
  const dL = a[0]! - b[0]!;
  const da = a[1]! - b[1]!;
  const db = a[2]! - b[2]!;
  return Math.sqrt(dL * dL + da * da + db * db);
}
